import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { dateToMonthAndYear } from '../../../../services/dateFormatter';

const getNodeLabelAndAvatar = (node) => {
  if (!node) return { name: '', avatarUrl: '', currentJobTitle: '' };
  if (node.kind === 'user') {
    const attrs = node.user?.data?.attributes || {};
    return {
      name: attrs.name || '',
      avatarUrl: attrs.avatarUrl || '',
      currentJobTitle: attrs.currentJobTitle || '',
    };
  }
  const attrs = node.contact?.data?.attributes || {};
  return {
    name: attrs.fullName || '',
    avatarUrl: attrs.profilePicUrl || '',
    currentJobTitle: attrs.currentJobTitle || '',
  };
};

const formatOverlapInfo = (node) => {
  const fromFormatted = node.overlapFrom ? dateToMonthAndYear(node.overlapFrom) : '';
  const toFormatted = node.overlapTo ? dateToMonthAndYear(node.overlapTo) : '';
  const org = node.organizationName ? `at ${node.organizationName} ` : '';

  return node.bothCurrentlyAtCompany
    ? `Work overlap: currently ${org}(since ${fromFormatted})`
    : `Work overlap: ${org}(${fromFormatted} – ${toFormatted})`;
};

const getSignalProperties = (type, node) => {
  const properties = {
    linkedin: type === 'linkedin_connection',
    google: type === 'google_connection',
    workOverlap: type === 'work_overlap',
  };

  const extraInfo = [];
  if (type === 'linkedin_connection') {
    extraInfo.push(
      `LinkedIn: 1st-degree connection${
        node.connectedAt && node.connectedAt !== 0 ? ` (since ${dateToMonthAndYear(node.connectedAt)})` : ''
      }`,
    );
  }
  if (type === 'google_connection') {
    extraInfo.push('Interaction: emails or meetings detected');
  }
  if (type === 'work_overlap') {
    extraInfo.push(formatOverlapInfo(node));
  }

  return { ...properties, extraInfo };
};

export function useConnectionPathCard({ item }) {
  const currentUserId = useSelector((s) => s.user?.id);
  const pathNodes = useMemo(() => item?.path || [], [item?.path]);

  return useMemo(() => {
    const nodesForRender = [];

    pathNodes.forEach((node, index) => {
      if (node.kind === 'user' || node.kind === 'contact') {
        const info = getNodeLabelAndAvatar(node);
        const key = `${node.kind}-${node.id}-${index}`;
        const isNodeYou =
          node.kind === 'user' &&
          (String(node.id) === String(currentUserId) || String(node?.user?.data?.id) === String(currentUserId));
        const labelName = isNodeYou ? 'You' : info.name || `${node.kind}-${node.id}`;
        const avatarName = info.name || `${node.kind}-${node.id}`;

        nodesForRender.push({
          id: node.id,
          key,
          labelName,
          avatarName,
          avatarUrl: info.avatarUrl,
          kind: node.kind,
          currentJobTitle: info.currentJobTitle,
        });
      } else {
        // Handle signal nodes
        const lastRenderedNode = nodesForRender[nodesForRender.length - 1];
        const signalProps = getSignalProperties(node.type, node);

        if (lastRenderedNode?.kind === 'signal') {
          // Update existing signal node
          Object.assign(lastRenderedNode, {
            linkedin: lastRenderedNode.linkedin || signalProps.linkedin,
            google: lastRenderedNode.google || signalProps.google,
            workOverlap: lastRenderedNode.workOverlap || signalProps.workOverlap,
          });
          lastRenderedNode.extraInfo.push(...signalProps.extraInfo);
        } else {
          // Create new signal node
          nodesForRender.push({
            kind: 'signal',
            key: `${lastRenderedNode?.kind}-${lastRenderedNode?.id}-${node.type}`,
            ...signalProps,
          });
        }
      }
    });

    return {
      nodesForRender,
    };
  }, [currentUserId, pathNodes]);
}
